'use client';

import { useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { ApiClientError } from '@/shared/lib/api';
import { cn } from '@/shared/lib/cn';
import { useSubscriptionLimits } from '@/features/subscription';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import { ProBadge } from '@/features/subscription/ui/ProBadge';
import { ProjectGoalsSheet } from '@/features/project/ui/ProjectGoalsSheet';
import { useEstimateDetail, useEstimateActions } from '../hooks/use-estimate';
import { useEstimateGoalsSheet } from '../hooks/use-estimate-goals-sheet';
import { useEstimateItems } from '../hooks/use-estimate-items';
import { useDebouncedCommit } from '../hooks/use-debounced-commit';
import {
  EstimateSettingsList,
  toEstimateSettingsValues,
} from './EstimateHeader';
import { EstimateTitleTags } from './EstimateTitleTags';
import { EstimateCurrencySheet } from './EstimateCurrencySheet';
import { EstimateSection } from './EstimateSection';
import { EstimateTotals } from './EstimateTotals';
import {
  COST_ESTIMATE_OVERWRITE_REQUIRED,
  type EstimateSection as Section,
  type UpdateEstimateItemRequest,
  type UpdateEstimateRequest,
} from '../api/estimate-service';

interface EstimateEditorProps {
  conceptId: string;
  projectId: string;
  estimateId: string;
  locale: string;
  /** Two-column layout: the settings list + staleness banner + regenerate CTA
   *  live in the inspector aside (EstimateSettingsPanel), so the editor body
   *  must not render its inline copies. */
  settingsHostedExternally?: boolean;
}

const FIELD_DEBOUNCE_MS = 600;
const HEADER_KEY = 'header';

/**
 * Single-estimate editor. Renders the header + Materials/Labor sections when
 * the estimate is `ready`; otherwise a pending/generating spinner or a failed
 * banner with retry. Header and item edits are debounced PATCHes; item
 * mutations trigger a server-side total recompute that arrives over WS, so the
 * detail query is invalidated after every item mutation.
 */
export function EstimateEditor({
  conceptId,
  projectId,
  estimateId,
  locale,
  settingsHostedExternally = false,
}: EstimateEditorProps) {
  const t = useTranslations('common.estimate.editor');

  // Cost estimate is a Pro perk (EntitlementFeatureMap: cost_estimate) — a
  // locked Regenerate tap upsells the Pro Pass instead of firing the request.
  const limits = useSubscriptionLimits();
  const hasCostEstimate = limits?.has_cost_estimate ?? false;
  const openPassPaywall = usePassPaywallStore((s) => s.open);

  const { data: estimate, isLoading } = useEstimateDetail(estimateId);
  const {
    regenerateEstimate,
    isRegenerating,
    updateEstimate,
    changeCurrency,
    isChangingCurrency,
    changeMeasurementSystem,
    isChangingMeasurement,
    changeLanguage,
    isChangingLanguage,
  } = useEstimateActions(projectId, conceptId);
  const {
    createItem,
    updateItem,
    applyItemDraft,
    deleteItem,
    reorderItems,
    lastAddedItemId,
    clearAddedItemHighlight,
  } = useEstimateItems(conceptId, estimateId);

  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  // Independent currency-picker dialog — opened by the currency tag next to
  // the title. Deliberately NOT one of the settings NestedSheet drill-downs:
  // the currency switch re-denominates the visible estimate immediately,
  // while the settings sheets hold generation inputs.
  const [showCurrencySheet, setShowCurrencySheet] = useState(false);

  // The region/facade-area rows open the shared project-goals sheet
  // (NestedSheet) so the user stays on the Estimate tab — Apple/Android sheet
  // parity, no page navigation (close → flush + estimate re-fetch — see the
  // hook).
  const { showGoalsSheet, handleGoalsSheetChange } = useEstimateGoalsSheet(
    projectId,
    estimateId
  );

  // Accumulate header field edits per debounce window so multiple fast field
  // changes flush as one PATCH with the latest values.
  const headerDraftRef = useRef<UpdateEstimateRequest>({});

  const headerCommit = useDebouncedCommit<UpdateEstimateRequest>(
    (_key, value) => {
      if (Object.keys(value).length === 0) return;
      updateEstimate(estimateId, value);
      headerDraftRef.current = {};
    },
    FIELD_DEBOUNCE_MS
  );

  // Per-item debounced PATCH — each item id has its own pending timer so a
  // stale edit on one row can't clobber another (debounce + clear race).
  const itemDraftsRef = useRef<Map<string, UpdateEstimateItemRequest>>(
    new Map()
  );
  const itemCommit = useDebouncedCommit<UpdateEstimateItemRequest>(
    (itemId, value) => {
      if (Object.keys(value).length === 0) return;
      updateItem({ itemId, data: value });
      itemDraftsRef.current.delete(itemId);
    },
    FIELD_DEBOUNCE_MS
  );

  const handleHeaderFieldChange = (data: UpdateEstimateRequest) => {
    const merged = { ...headerDraftRef.current, ...data };
    headerDraftRef.current = merged;
    headerCommit.schedule(HEADER_KEY, merged);
  };

  // Latest display currency / measurement system for stamping item patches at
  // scheduling time — refs so the stable debounced-commit callbacks never read
  // a stale closure.
  const currencyRef = useRef(estimate?.currency);
  currencyRef.current = estimate?.currency;
  const measurementRef = useRef(estimate?.measurement_system);
  measurementRef.current = estimate?.measurement_system;

  const handleItemFieldChange = (
    itemId: string,
    data: UpdateEstimateItemRequest
  ) => {
    // Instant cache write so the row total AND the totals summary recompute on
    // this very keystroke — the debounced PATCH below reconciles with the
    // server later.
    applyItemDraft(itemId, data);
    const merged = { ...(itemDraftsRef.current.get(itemId) ?? {}), ...data };
    // Stamp the display currency the price was typed in (stale-device guard):
    // the server 422s when the echo mismatches the estimate's current currency,
    // so a price entered on a device that missed a currency switch can't land
    // as a wrongly-denominated amount.
    if (merged.unit_price !== undefined && currencyRef.current) {
      merged.currency = currencyRef.current;
    }
    // Same guard for the measurement system: quantity/unit/unit_price are all
    // denominated in the display system the user is looking at.
    if (
      (merged.quantity !== undefined ||
        merged.unit !== undefined ||
        merged.unit_price !== undefined) &&
      measurementRef.current
    ) {
      merged.measurement_system = measurementRef.current;
    }
    itemDraftsRef.current.set(itemId, merged);
    itemCommit.schedule(itemId, merged);
  };

  const { materialsItems, laborItems } = useMemo(() => {
    // Re-sort by sort_order: the fetch is pre-sorted, but the optimistic
    // reorder re-stamps sort_order in the cache without re-shuffling the array.
    const items = [...(estimate?.items ?? [])].sort(
      (a, b) => a.sort_order - b.sort_order
    );
    return {
      materialsItems: items.filter((i) => i.section === 'materials'),
      laborItems: items.filter((i) => i.section === 'labor'),
    };
  }, [estimate?.items]);

  const handleAddItem = (section: Section) => {
    const siblings = section === 'materials' ? materialsItems : laborItems;
    const maxSort = siblings.reduce(
      (max, i) => Math.max(max, i.sort_order),
      -1
    );
    createItem({
      section,
      name: t('newItemName'),
      quantity: 1,
      unit_price: 0,
      sort_order: maxSort + 1,
      // Currency + measurement-system echoes (stale-device guards) — see
      // handleItemFieldChange.
      ...(currencyRef.current ? { currency: currencyRef.current } : {}),
      ...(measurementRef.current
        ? { measurement_system: measurementRef.current }
        : {}),
    });
  };

  const runRegenerate = async (confirmOverwrite?: boolean) => {
    try {
      await regenerateEstimate({ estimateId, confirmOverwrite });
      setShowOverwriteConfirm(false);
    } catch (error) {
      // Edited estimate → server demands explicit confirmation. Open the
      // confirm dialog; the retry sends confirm_overwrite=true.
      if (
        error instanceof ApiClientError &&
        error.code === COST_ESTIMATE_OVERWRITE_REQUIRED
      ) {
        setShowOverwriteConfirm(true);
        return;
      }
      // Other errors already surfaced as a toast in the mutation onError.
    }
  };

  if (isLoading || !estimate) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Pending / generating — generation is in flight. Capped at the intro's
  // readable column width: edge-to-edge copy on the wide two-column layout
  // reads as a stretched placeholder (Apple/Android parity).
  if (estimate.status === 'pending' || estimate.status === 'generating') {
    return (
      <div className="mx-auto flex w-full max-w-[560px] flex-col items-center justify-center gap-3 py-16 text-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-list-item-subtitle text-foreground">
          {t('generating')}
        </p>
        <p className="text-card-meta text-muted-foreground">
          {t('generatingHint')}
        </p>
        <p className="text-card-meta text-muted-foreground/70">
          {t('generatingAccuracyHint')}
        </p>
      </div>
    );
  }

  // Failed — surface the server-owned reason and offer a retry. Same
  // readable cap as the generating state above.
  if (estimate.status === 'failed') {
    return (
      <div className="mx-auto w-full max-w-[560px] space-y-4">
        <NoticeBanner
          variant="error"
          title={t('failedTitle')}
          message={estimate.failure_reason ?? undefined}
        />
        <div className="flex justify-center">
          <Button
            variant="teal"
            size="cta"
            onClick={() => runRegenerate()}
            disabled={isRegenerating}
          >
            {isRegenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('retryGeneration')}
          </Button>
        </div>
      </div>
    );
  }

  // Ready — full editor. A currency or measurement-system switch rewrites
  // every amount server-side, so the whole editor is locked while in flight.
  const isBusy =
    isRegenerating ||
    isChangingCurrency ||
    isChangingMeasurement ||
    isChangingLanguage;
  return (
    <>
      <div className={cn('flex flex-col gap-3 pb-2')}>
        {/* Export lives in the concept page "⋯" menu ("Export estimate"
            submenu in ConceptActions) — no inline export affordance here. */}

        {/* Title + the display-denomination tag-toggles (currency picker /
            instant units toggle). The title renders in BOTH layouts, so the
            tags are visible whether or not the settings live in the inspector
            aside. Title leads, tags pin to the trailing edge; a long
            localized title wraps in its own column instead of pushing the
            tags off-screen. */}
        <div className="flex items-center gap-2 px-1">
          <h2 className="text-card-title text-foreground flex-1 min-w-0">
            {t('title')}
          </h2>
          <EstimateTitleTags
            currency={estimate.currency}
            measurementSystem={estimate.measurement_system}
            disabled={isBusy}
            onOpenCurrencyPicker={() => setShowCurrencySheet(true)}
            isChangingCurrency={isChangingCurrency}
            onMeasurementToggle={() =>
              changeMeasurementSystem(
                estimateId,
                estimate.measurement_system === 'metric'
                  ? 'imperial'
                  : 'metric'
              )
            }
            isChangingMeasurement={isChangingMeasurement}
          />
        </div>

        {/* Totals summary card LEADS the estimate (the headline numbers come
            first) — the line items that produced them follow. */}
        <EstimateTotals estimate={estimate} />

        <EstimateSection
          section="materials"
          items={materialsItems}
          notes={estimate.materials_notes}
          unitOptions={estimate.unit_options ?? []}
          disabled={isBusy}
          onFieldChange={handleItemFieldChange}
          onFieldCommit={(itemId) => itemCommit.flush(itemId)}
          onNotesChange={(value) =>
            handleHeaderFieldChange({ materials_notes: value })
          }
          onNotesCommit={() => headerCommit.flush(HEADER_KEY)}
          onDeleteItem={deleteItem}
          onAddItem={handleAddItem}
          onReorderItems={(section, itemIds) =>
            reorderItems({ section, itemIds })
          }
          lastAddedItemId={lastAddedItemId}
          onAddedItemShown={clearAddedItemHighlight}
        />

        <EstimateSection
          section="labor"
          items={laborItems}
          notes={estimate.labor_notes}
          unitOptions={estimate.unit_options ?? []}
          disabled={isBusy}
          onFieldChange={handleItemFieldChange}
          onFieldCommit={(itemId) => itemCommit.flush(itemId)}
          onNotesChange={(value) =>
            handleHeaderFieldChange({ labor_notes: value })
          }
          onNotesCommit={() => headerCommit.flush(HEADER_KEY)}
          onDeleteItem={deleteItem}
          onAddItem={handleAddItem}
          onReorderItems={(section, itemIds) =>
            reorderItems({ section, itemIds })
          }
          lastAddedItemId={lastAddedItemId}
          onAddedItemShown={clearAddedItemHighlight}
        />

        {/* Editing affordances are easy to miss in a dense table — spell them
            out once at the end of the estimate. */}
        <NoticeBanner variant="info" message={t('editHintBanner')} />

        {/* Stacked (single-column): the settings list sits AFTER the estimate,
            right next to the regenerate CTA — the staleness banner rides above
            the group (not tied to a single setting). Two-column hosts all of
            these in the inspector aside (EstimateSettingsPanel) — rendering
            them here too would double them. */}
        {!settingsHostedExternally && (
          // These are SETTINGS, not the estimate document — capped to the
          // readable form width on wide single-column screens (centered) so they
          // read as narrow settings below the full-width materials/labor table
          // above. Mirrors the Views/Blueprint forms. Apple/Android parity.
          <div className="mx-auto flex w-full max-w-[560px] flex-col gap-3">
            {/* Server-computed staleness flag (`has_newer_renders`): the
                concept gained completed renders after this estimate was
                calculated, so the numbers may not reflect the latest
                designs. */}
            {estimate.has_newer_renders && (
              <NoticeBanner
                variant="warning"
                message={t('newerRendersWarning')}
              />
            )}
            <EstimateSettingsList
              values={toEstimateSettingsValues(estimate)}
              disabled={isBusy}
              onLanguageChange={(language) =>
                changeLanguage(estimateId, language)
              }
              isChangingLanguage={isChangingLanguage}
              onOpenProjectGoals={() => handleGoalsSheetChange(true)}
              onSpecialRequirementsSave={(value) =>
                updateEstimate(estimateId, { special_requirements: value })
              }
            />
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="cta"
                onClick={() =>
                  hasCostEstimate ? runRegenerate() : openPassPaywall('pro')
                }
                disabled={isRegenerating}
              >
                {isRegenerating && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('regenerate')}
                <ProBadge tier="pro" isLocked={!hasCostEstimate} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Independent currency-picker dialog (modal, not a NestedSheet
          drill-down) — opened by the currency tag. */}
      <EstimateCurrencySheet
        open={showCurrencySheet}
        onOpenChange={setShowCurrencySheet}
        value={estimate.currency}
        locale={locale}
        onSelect={(currency) => changeCurrency(estimateId, currency)}
      />

      {/* Shared project-goals editor (goals + region + facade area) — opened
          by the facade-area link in the header. */}
      <ProjectGoalsSheet
        projectId={projectId}
        open={showGoalsSheet}
        onOpenChange={handleGoalsSheetChange}
      />

      {/* Overwrite confirm — only shown when the estimate was hand-edited and
          the server requested explicit confirmation (409). */}
      <AlertDialog
        open={showOverwriteConfirm}
        onOpenChange={setShowOverwriteConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('overwriteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('overwriteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRegenerating}>
              {t('overwriteCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runRegenerate(true)}
              disabled={isRegenerating}
            >
              {t('overwriteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
